import { Suspense, useEffect } from 'react';
import { Route, Routes, useLocation } from 'react-router';

import { useTracking } from '@/hooks/useTracking';
import { useNewAuth } from '@/providers/AuthProvider/NewAuthProvider.hooks';
import { lazyWithReload } from '@/lib/chunkRecovery';

import { PrivateRoute } from './PrivateRoute';
import { LimitedAccessToTheEntitiesRoute } from '@/routes/LimitedAccessToTheEntitiesRoute';
import { EnabledEntityRoute } from '@/routes/EnabledEntityRoute';
import { SupportedStablecoinRoute } from '@/routes/SupportedStablecoinRoute';
import { PermittedRoute } from '@/routes/PermittedRoute';
import { RedirectCoordinator } from '@/routes/RedirectCoordinator';
import { AuthFallback, SmartFallback } from '@/routes/SmallFallback';
import { useNewFeature } from '@/providers/NewFeatureContextProvider/NewFeatureContext.hooks';
import { usePermissionsManager } from '@/hooks/usePermissionsManager';

import { DashboardPage } from '@/pages/DashboardPage';

const SettingsPage = lazyWithReload(() => import('@/pages/SettingsPage'));
const RiskSimulatorPage = lazyWithReload(() => import('@/pages/RiskSimulator/RiskSumulatorPage'));
const RiskSimulatorPortfolioPage = lazyWithReload(() =>
  import('@/pages/RiskSimulator/RiskSimulatorPortfolioPage').then(module => ({
    default: module.RiskSimulatorPortfolioPage,
  })),
);
const DocumentationPage = lazyWithReload(() => import('@/pages/Documentation/DocumentationPage'));
const DashboardSettingsPage = lazyWithReload(() =>
  import('@/pages/DashboardSettingsPage').then(module => ({
    default: module.DashboardSettingsPage,
  })),
);
const EntityPage = lazyWithReload(() =>
  import('@/pages/EntityPage').then(module => ({ default: module.EntityPage })),
);
const LoginPage = lazyWithReload(() => import('@/pages/LoginPage'));
const ForgotPasswordPage = lazyWithReload(() => import('@/pages/ForgotPasswordPage'));
const OnboardingPage = lazyWithReload(() => import('@/pages/OnboardingPage'));
const ResetPasswordPage = lazyWithReload(() => import('@/pages/ResetPasswordPage'));
const ReservesMonitoringPage = lazyWithReload(() =>
  import('@/pages/ReservesMonitoring').then(module => ({ default: module.ReservesMonitoringPage })),
);
const StablecoinsDashboardPage = lazyWithReload(() =>
  import('@/pages/StablecoinsDashboard').then(module => ({
    default: module.StablecoinsDashboardPage,
  })),
);
const AlertingPage = lazyWithReload(() =>
  import('@/pages/AlertingPage').then(module => ({ default: module.AlertingPage })),
);

import MainLayout from '@/layouts/MainLayout/MainLayout';

import { CommonError } from '@/components/Error/CommonError/CommonError';
import ErrorBoundary from '@/components/Error/ErrorBoundary/ErrorBoundary';
import { StyledPageLoader } from '@/components/StyledSharedComponents/StyledLoader';
import { isLegacyRoute } from '@/routes/routePatterns';
import { ERoutes } from '@/types/routes';

const AppRoutes = () => {
  const { trackPageView } = useTracking();
  const { isAuthenticated, redirectLoggedInUser } = useNewAuth();

  const location = useLocation();

  useEffect(() => {
    trackPageView(location.pathname, location.search);
  }, [location?.pathname, location?.search, trackPageView]);

  // Redirect logged-in user trying to access old routes
  useEffect(() => {
    if (isAuthenticated) {
      if (isLegacyRoute(location.pathname)) {
        const redirectTo = location.pathname + location.search + location.hash;
        redirectLoggedInUser(redirectTo);
      }
    }
    // This redirect check is intentionally mount-only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <RedirectCoordinator />
      <WebAppRoutes />
    </>
  );
};

export default AppRoutes;

const WebAppRoutes = () => {
  const { isAlertingPermitted } = useNewFeature();
  const {
    shouldDisplayRiskMonitoring,
    shouldDisplayAlerting,
    shouldDisplayAlertingSettings,
    shouldDisplayStablecoins,
    shouldDisplayModeling,
    shouldDisplayDocumentation,
  } = usePermissionsManager();

  return (
    <Suspense fallback={<StyledPageLoader />}>
      <Routes>
        <Route
          path={ERoutes.DASHBOARD}
          element={
            <PrivateRoute>
              <MainLayout header={'dashboard'}>
                <PermittedRoute isPermitted={shouldDisplayRiskMonitoring}>
                  <DashboardPage />
                </PermittedRoute>
              </MainLayout>
            </PrivateRoute>
          }
        />
        <Route
          path={ERoutes.DASHBOARD_SETTINGS}
          element={
            <PrivateRoute>
              <MainLayout header={'dashboardSettings'}>
                <PermittedRoute
                  isPermitted={shouldDisplayRiskMonitoring || shouldDisplayAlertingSettings}
                >
                  <DashboardSettingsPage />
                </PermittedRoute>
              </MainLayout>
            </PrivateRoute>
          }
        />
        <Route
          path={`${ERoutes.ENTITY}/:entityName`}
          element={
            <PrivateRoute>
              <LimitedAccessToTheEntitiesRoute>
                <MainLayout header={'entity'}>
                  <PermittedRoute isPermitted={shouldDisplayRiskMonitoring}>
                    <ErrorBoundary fallback={<CommonError variant="reload" />}>
                      <EnabledEntityRoute>
                        <EntityPage />
                      </EnabledEntityRoute>
                    </ErrorBoundary>
                  </PermittedRoute>
                </MainLayout>
              </LimitedAccessToTheEntitiesRoute>
            </PrivateRoute>
          }
        />
        <Route
          path={ERoutes.RISK_SIMULATOR}
          element={
            <PrivateRoute>
              <MainLayout header={'riskSimulator'}>
                <PermittedRoute isPermitted={shouldDisplayModeling}>
                  <RiskSimulatorPage />
                </PermittedRoute>
              </MainLayout>
            </PrivateRoute>
          }
        />
        <Route
          path={ERoutes.RISK_SIMULATOR_PORTFOLIO}
          element={
            <PrivateRoute>
              <MainLayout header={'riskSimulator'}>
                <PermittedRoute isPermitted={shouldDisplayModeling}>
                  <RiskSimulatorPortfolioPage />
                </PermittedRoute>
              </MainLayout>
            </PrivateRoute>
          }
        />
        <Route
          path={ERoutes.STABLECOINS}
          element={
            <PrivateRoute>
              <MainLayout header={'stablecoins'}>
                <PermittedRoute isPermitted={shouldDisplayStablecoins}>
                  <StablecoinsDashboardPage />
                </PermittedRoute>
              </MainLayout>
            </PrivateRoute>
          }
        />
        <Route
          path={`${ERoutes.STABLECOINS}/:agioId`}
          element={
            <PrivateRoute>
              <SupportedStablecoinRoute>
                <MainLayout header={'stablecoinDetail'}>
                  <PermittedRoute isPermitted={shouldDisplayStablecoins}>
                    <ReservesMonitoringPage />
                  </PermittedRoute>
                </MainLayout>
              </SupportedStablecoinRoute>
            </PrivateRoute>
          }
        />
        {isAlertingPermitted && (
          <Route
            path={`${ERoutes.ALERTING}/:alertId?`}
            element={
              <PrivateRoute>
                <MainLayout header={'alerting'}>
                  <PermittedRoute isPermitted={shouldDisplayAlerting}>
                    <AlertingPage />
                  </PermittedRoute>
                </MainLayout>
              </PrivateRoute>
            }
          />
        )}
        <Route
          path={`${ERoutes.DOCUMENTATION}/*`}
          element={
            <PrivateRoute>
              <MainLayout header={'documentation'} isFlush>
                <PermittedRoute isPermitted={shouldDisplayDocumentation}>
                  <DocumentationPage />
                </PermittedRoute>
              </MainLayout>
            </PrivateRoute>
          }
        />
        <Route
          path={ERoutes.SETTINGS}
          element={
            <PrivateRoute>
              <MainLayout header={'settings'}>
                <SettingsPage />
              </MainLayout>
            </PrivateRoute>
          }
        />
        <Route path={ERoutes.LOGIN} element={<AuthFallback PageComponent={LoginPage} />} />
        <Route
          path={ERoutes.FORGOT_PASSWORD}
          element={<AuthFallback PageComponent={ForgotPasswordPage} />}
        />
        <Route path={`${ERoutes.ONBOARDING}/:token`} element={<OnboardingPage />} />
        <Route path={ERoutes.RESET_PASSWORD} element={<ResetPasswordPage />} />
        <Route path="*" element={<SmartFallback />} />
      </Routes>
    </Suspense>
  );
};
